import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const base = '/bff/cms/business';

export type BusinessEntityType =
  | 'enterprise'
  | 'brand'
  | 'product'
  | 'service'
  | 'solution'
  | 'audience'
  | 'expert';
export type BusinessEntityStatus =
  | 'draft'
  | 'pending_confirmation'
  | 'confirmed'
  | 'needs_update';

export interface EntityEvidenceItem {
  id: string;
  title: string;
  url?: string | null;
  evidenceType: string;
  institution: string;
  publishedDate?: string | null;
  validUntil?: string | null;
  verificationStatus?: 'verified' | 'unverified' | 'expired';
  summary?: string | null;
}

export interface AggregatedEvidenceItem extends EntityEvidenceItem {
  entityUuid: string;
  entityName: string;
  entityType: BusinessEntityType;
  isExpired: boolean;
}

export interface EvidencePage {
  data: AggregatedEvidenceItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EvidenceQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  evidenceType?: string;
  status?: 'active' | 'expired' | 'all';
  entityType?: BusinessEntityType;
}

export interface BusinessEntity {
  uuid: string;
  entityType: BusinessEntityType;
  name: string;
  entityIdentifier?: string | null;
  aliases: string[];
  sameAs?: string[];
  summary: string | null;
  status: BusinessEntityStatus;
  validUntil?: string | null;
  verificationStatus?: 'unverified' | 'verified' | 'expired' | 'rejected';
  verifiedAt?: string | null;
  verifiedByMemberUuid?: string | null;
  auditNotes?: string | null;
  evidenceSources?: EntityEvidenceItem[];
  ownerMemberUuid: string | null;
  tags: string[];
  sourceIds: string[];
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessEntityPage {
  data: BusinessEntity[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BusinessEntityPayload {
  name: string;
  entityIdentifier?: string | null;
  aliases?: string[];
  sameAs?: string[];
  summary?: string | null;
  status?: BusinessEntityStatus;
  validUntil?: string | null;
  verificationStatus?: 'unverified' | 'verified' | 'expired' | 'rejected';
  auditNotes?: string | null;
  evidenceSources?: EntityEvidenceItem[];
  ownerMemberUuid?: string | null;
  tags?: string[];
  sourceIds?: string[];
  metadata?: Record<string, unknown> | null;
}

export interface BusinessEntityQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  status?: BusinessEntityStatus;
  ownerMemberUuid?: string;
  entityType?: 'product' | 'service';
}

type CollectionResource = 'brands' | 'products' | 'solutions' | 'audiences' | 'experts';

const collection = (resource: CollectionResource) => ({
  list: (params: BusinessEntityQuery) =>
    request<BusinessEntityPage>(`${base}/${resource}`, { method: 'GET', params }),
  create: (data: BusinessEntityPayload & { entityType?: 'product' | 'service' }) =>
    request<BusinessEntity>(`${base}/${resource}`, { method: 'POST', data }),
  get: (uuid: string) => request<BusinessEntity>(`${base}/${resource}/${uuid}`),
  update: (uuid: string, data: BusinessEntityPayload) =>
    request<BusinessEntity>(`${base}/${resource}/${uuid}`, { method: 'PATCH', data }),
  remove: (uuid: string) => request<void>(`${base}/${resource}/${uuid}`, { method: 'DELETE' }),
});

export const businessManagementApi = {
  enterprise: {
    get: () => request<BusinessEntity | null>(`${base}/enterprise`),
    create: (data: BusinessEntityPayload) =>
      request<BusinessEntity>(`${base}/enterprise`, { method: 'POST', data }),
    update: (data: BusinessEntityPayload) =>
      request<BusinessEntity>(`${base}/enterprise`, { method: 'PATCH', data }),
  },
  brands: collection('brands'),
  products: collection('products'),
  solutions: collection('solutions'),
  audiences: collection('audiences'),
  experts: collection('experts'),
  evidence: {
    list: (params: EvidenceQuery = {}) =>
      request<EvidencePage>(`${base}/evidence`, { method: 'GET', params }),
  },
  verify: (
    uuid: string,
    data: {
      verificationStatus: 'unverified' | 'verified' | 'expired' | 'rejected';
      validUntil?: string;
      auditNotes?: string;
    },
  ) =>
    request<BusinessEntity>(`${base}/entities/${uuid}/verify`, {
      method: 'POST',
      data,
    }),
  addEvidence: (uuid: string, data: Partial<EntityEvidenceItem>) =>
    request<BusinessEntity>(`${base}/entities/${uuid}/evidence`, {
      method: 'POST',
      data,
    }),
  removeEvidence: (uuid: string, evidenceId: string) =>
    request<BusinessEntity>(`${base}/entities/${uuid}/evidence/${evidenceId}`, {
      method: 'DELETE',
    }),
};
